import { Controller } from '@hotwired/stimulus'
import { loadMercadoPago } from '~/lib/mercado_pago_sdk'

// Cartão via Secure Fields do Mercado Pago: número, validade e CVV ficam em
// iframes do MP. O servidor recebe só o token de uso único e a bandeira.
// Sem public key (modo mock) não faz nada.
export default class extends Controller {
  static targets = ['cardNumber', 'expiration', 'securityCode', 'holder', 'token', 'brand', 'installments', 'cpf', 'submit']
  static values = { publicKey: String, amountCents: Number }

  async connect() {
    if (!this.publicKeyValue || !this.hasCardNumberTarget) return
    // Checkout: total vem do checkout_controller; compra na área: valor fixo do produto.
    this.totalCents = this.amountCentsValue || Number(this.element.dataset.checkoutBaseCentsValue)
    this.element.addEventListener('checkout:total', (e) => {
      this.totalCents = e.detail.totalCents
      this.loadInstallments()
    })

    try {
      this.mp = await loadMercadoPago(this.publicKeyValue)
      this.cardNumber = this.mp.fields.create('cardNumber', { placeholder: 'Número do cartão' }).mount(this.cardNumberTarget.id)
      this.mp.fields.create('expirationDate', { placeholder: 'MM/AA' }).mount(this.expirationTarget.id)
      this.mp.fields.create('securityCode', { placeholder: 'CVV' }).mount(this.securityCodeTarget.id)
      this.cardNumber.on('binChange', ({ bin }) => this.onBin(bin))
    } catch (error) {
      console.error(error)
    }
  }

  async onBin(bin) {
    this.bin = bin
    if (!bin) return
    const methods = await this.mp.getPaymentMethods({ bin })
    this.brandTarget.value = methods?.results?.[0]?.id || ''
    this.loadInstallments()
  }

  // Parcelas oficiais do MP (com juros, se houver), limitadas a 6x.
  async loadInstallments() {
    if (!this.mp || !this.bin) return
    const result = await this.mp.getInstallments({ amount: String(this.totalCents / 100), bin: this.bin, paymentTypeId: 'credit_card' })
    const costs = (result?.[0]?.payer_costs || []).filter((c) => c.installments <= 6)
    if (!costs.length) return
    const select = this.installmentsTarget
    select.dataset.mpManaged = 'true'
    select.innerHTML = ''
    costs.forEach((c) => select.add(new Option(c.recommended_message, c.installments)))
  }

  async submit(event) {
    if (!this.mp || this.method() !== 'card' || this.tokenTarget.value) return
    event.preventDefault()
    if (this.busy) return
    this.busy = true
    this.submitTarget.disabled = true

    try {
      const cpf = (this.hasCpfTarget ? this.cpfTarget.value : '').replace(/\D/g, '')
      const token = await this.mp.fields.createCardToken({
        cardholderName: this.holderTarget.value.trim(),
        identificationType: 'CPF',
        identificationNumber: cpf
      })
      if (!token?.id) throw new Error('Não foi possível validar o cartão.')
      // binChange nem sempre dispara (autopreenchimento, colar): sem bandeira o MP recusa a ordem.
      if (!this.brandTarget.value && token.first_six_digits) {
        const methods = await this.mp.getPaymentMethods({ bin: token.first_six_digits })
        this.brandTarget.value = methods?.results?.[0]?.id || ''
      }
      if (!this.brandTarget.value) throw new Error('Não foi possível identificar a bandeira do cartão. Confira o número.')
      this.tokenTarget.value = token.id
      this.element.requestSubmit()
    } catch (error) {
      window.alert(error?.message || 'Confira os dados do cartão.')
    } finally {
      this.busy = false
      this.submitTarget.disabled = false
    }
  }

  method() {
    return this.element.querySelector('input[name="checkout[payment_method]"]:checked, input[name="payment_method"]:checked')?.value
  }
}
